import React, { useCallback, useEffect, useState } from 'react'
import { Button, message, Table, Tag, Tooltip, Typography } from 'antd'
import { ReloadOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import api from '../../app/api'
import { AdminLayout } from '../../components/AdminLayout'
import { useTranslation } from '../../shared/hooks/useTranslation'
import style from './AdminUploadHistory.module.scss'

const { Title, Text } = Typography

interface HistoryItem {
  id: number
  user_id?: number
  user_phone?: string
  user_name?: string
  file_name: string
  file_size: number
  status: 'success' | 'failed'
  total_records: number
  russian_count: number
  kazakh_count: number
  english_count: number
  error_message?: string
  created_at: string
}

function formatBytes(bytes: number): string {
  if (!bytes) return '—'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`
}

export function AdminUploadHistory() {
  const { t } = useTranslation()
  const [items, setItems] = useState<HistoryItem[]>([])
  const [loading, setLoading] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.get('/admin/services-upload-history', {
        headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` },
      })
      setItems(res.data?.items || [])
    } catch (err: any) {
      message.error(t.adminUploadHistoryLoadError)
    } finally {
      setLoading(false)
    }
  }, [t.adminUploadHistoryLoadError])

  useEffect(() => {
    load()
  }, [load])

  return (
    <AdminLayout>
      <div className={style.content}>
        <div className={style.header}>
          <div>
            <Title level={4} className={style.pageTitle}>
              {t.adminUploadHistoryTitle}
            </Title>
            <Text type="secondary">{t.adminUploadHistorySubtitle}</Text>
          </div>
          <Button
            icon={<ReloadOutlined />}
            onClick={load}
            loading={loading}
          >
            {t.adminUploadHistoryRefresh}
          </Button>
        </div>

        <Table<HistoryItem>
          rowKey="id"
          size="small"
          dataSource={items}
          loading={loading}
          pagination={{ pageSize: 20, showSizeChanger: false }}
          className={style.table}
          locale={{ emptyText: t.adminUploadHistoryEmpty }}
          columns={[
            {
              title: t.adminUploadHistoryDate,
              dataIndex: 'created_at',
              width: 160,
              render: (v: string) => dayjs(v).format('DD.MM.YYYY HH:mm'),
            },
            {
              title: t.adminUploadHistoryUser,
              render: (_, r) => r.user_name || r.user_phone || '—',
            },
            {
              title: t.adminUploadHistoryFile,
              dataIndex: 'file_name',
              ellipsis: true,
            },
            {
              title: t.adminUploadHistorySize,
              dataIndex: 'file_size',
              width: 100,
              render: formatBytes,
            },
            {
              title: t.adminUploadHistoryStatus,
              dataIndex: 'status',
              width: 130,
              render: (s: 'success' | 'failed', r: HistoryItem) => {
                if (s === 'success') {
                  return <Tag color="success">{t.adminUploadHistoryStatusSuccess}</Tag>
                }
                return (
                  <Tooltip title={r.error_message || ''} placement="topLeft">
                    <Tag color="error" style={{ cursor: 'help' }}>
                      {t.adminUploadHistoryStatusFailed}
                    </Tag>
                  </Tooltip>
                )
              },
            },
            {
              title: t.adminUploadHistoryTotal,
              dataIndex: 'total_records',
              align: 'left',
              width: 90,
            },
            { title: 'RU', dataIndex: 'russian_count', align: 'left', width: 70 },
            { title: 'KK', dataIndex: 'kazakh_count', align: 'left', width: 70 },
            { title: 'EN', dataIndex: 'english_count', align: 'left', width: 70 },
          ]}
          expandable={{
            rowExpandable: (r) => !!r.error_message,
            expandedRowRender: (r) => (
              <div className={style.errorCell}>{r.error_message}</div>
            ),
          }}
        />
      </div>
    </AdminLayout>
  )
}
